import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import { USER_ROLES } from "../../helpers/constants";
import { useAccessControl } from "../../hooks/auth/useCheckPermission";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useTransfersIndividualBills } from "../../services/bankingService";
import TableService from "../../services/tableService";

import InvoicingNavigationBar from "../InvoicingInvestmentBillPage/InvoicingNavigrationBar"; 

import { useTransferTableFieldsConfig, TRANSFER_TABLE_NAME } from "../FinancialPanelTransferIndividualBillPage/TansferIndividualBillTableFieldsConfig";
import { useTableFieldsConfig as useInvoiceTableFieldsConfig, TABLE_NAME as INVOICE_TABLE_NAME } from "../InvoicingInvoicesPage/PageTableFieldsConfig";
import TransferCollapsibleTable from "./TransferCollapsibleTable";

import { InvestmentFilter } from "../../components/common/InvestmentFilter";
import { DateRangeFilter } from "../../components/common/DateRangeFilter/DateRangeFilter";

import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import { ChangeInvoiceIsBookedButton } from "../InvoicingInvoicesPage/ChangeInvoiceIsBookedButton";
import { BooleanFilter } from "../../components/common/BooleanFilter/BooleanFilter";

export default function InvoicingIndividualBillPage(props) {
    const { hasAccess } = useAccessControl([USER_ROLES.INVESTMENT_DPI, USER_ROLES.ADMIN, USER_ROLES.ACCOUNT]);

    if (!hasAccess){
        return null
    }

    const { pageName } = props;

    const { t } = useTranslation();
    const TRANSFER_TABLE_FIELDS_CONFIG = useTransferTableFieldsConfig().filter(config=>config.name!=='transfer_is_booked' && config.name!=='edit');
    const INVOICE_TABLE_FIELDS_CONFIG = useInvoiceTableFieldsConfig()
    
    const TABLE_CONFIGS = [
        { name: TRANSFER_TABLE_NAME, config: TRANSFER_TABLE_FIELDS_CONFIG },
        { name: INVOICE_TABLE_NAME, config: INVOICE_TABLE_FIELDS_CONFIG }
    ];

    const [dataLocal, setDataLocal] = useState([]);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState({});
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState({});
    const [recordsCount, setRecordsCount] = useState(0);
    const [selectedInvoices, setSelectedInvoices] = useState([])

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const transferSearchParams = useMemo(() => {
        const tempSearchParams = new URLSearchParams(searchParams)
        tempSearchParams.set("transfer_is_booked", true)
        tempSearchParams.set("only_connected", true);
        return tempSearchParams
    }, [searchParams])

    const { data: transferData, refetch, isLoading } = useTransfersIndividualBills(transferSearchParams);
    useEffect(() => {
        if (transferData?.results) {
            setDataLocal(transferData.results);
            setRecordsCount(transferData.count);
        }
    }, [transferData, dataPage, dataPageSize]);

    const handleClickInvoiceCheckBox = (invoiceId) => {
        setSelectedInvoices(prev=>{
            let currentInvoicesIds = [...prev]
            if (currentInvoicesIds.includes(invoiceId)) {
                currentInvoicesIds = currentInvoicesIds.filter(id => id !== invoiceId)
            }else {
                currentInvoicesIds.push(invoiceId)
            }

            return currentInvoicesIds

        })
    }

    const handleBookedInvoice = async ()=> {
        setSelectedInvoices(prev=>[])
    }

    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
    }, []);

    const trnasferDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            TRANSFER_TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[TRANSFER_TABLE_NAME] || [], // @ts-ignore
            columnsOrdersForTables?.[TRANSFER_TABLE_NAME] || [],
        );
    }, [TRANSFER_TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                TRANSFER_TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[TRANSFER_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[TRANSFER_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[TRANSFER_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[TRANSFER_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            TRANSFER_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );


    const tableComponent = () => (
        <TransferCollapsibleTable
            // @ts-ignore
            data={data}
            countRecords={recordsCount}
            resetPageNumber={resetPageNumber}
            style={{ maxHeight: "60vh" }}
            headersConfig={TableService.orderColumns(
                TableService.hideHiddenColumns(
                    TRANSFER_TABLE_FIELDS_CONFIG, // @ts-ignore
                    hiddenColumnsForTables?.[TRANSFER_TABLE_NAME] || [],
                ),
                // @ts-ignore
                columnsOrdersForTables?.[TRANSFER_TABLE_NAME] || [],
            )}
            innerTableHeadersConfig={TableService.orderColumns(
                TableService.hideHiddenColumns(
                    INVOICE_TABLE_FIELDS_CONFIG, // @ts-ignore
                    hiddenColumnsForTables?.[INVOICE_TABLE_NAME] || [],
                ),
                // @ts-ignore
                columnsOrdersForTables?.[INVOICE_TABLE_NAME] || [],
            )}
            showCleanFilterIcon={true}
            showContextMenu={false}
            showExportToFileButton={true}
            exportToFileUrl={"bankings/transfers/individual_bills/export/"}
            exportToFileSearchParams={transferSearchParams}
            exportToFileHeaders={trnasferDataExportHeaders}
            exportToFileFileName={`${t("bankings.exportTransferFileName")}.xlsx`}
            showDetailsIcon={false}
            showCheckbox={false}
            page={dataPage}
            onPageChange={handleChangeDataPage}
            rowsPerPage={dataPageSize}
            onRowsPerPageChange={handleChangeDataPageSize}
            setDataLocal={setDataLocal}
            isLoading={isLoading}
            hiddenColumnsForTables={hiddenColumnsForTables}
            columnsOrdersForTables={columnsOrdersForTables}
            onClickInnerTableCheckBox={handleClickInvoiceCheckBox}
        />
    );

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.invoicing_individual_bills")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                rowSpacing={1}
                columnSpacing={2}
            >
                <Grid item xs={12}>
                    < InvoicingNavigationBar />
                </Grid>
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={TABLE_CONFIGS}
                        refreshTable={refetch}
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={false}
                        showMyFiltersButton={false}
                        showSearchButton={false}
                        style={{ marginBlock: "5px" }}
                        extraButtonList={[
                            <InvestmentFilter
                                filterName={"transfer_invest"}
                                searchParams={searchParams}
                                setSearchParams={setSearchParams}
                                InputLabelStyle={{marginTop: "-6px"}}
                                maxHeight={"40px"}
                            />,
                            <BooleanFilter 
                                filterName={"has_invoice"}
                                filterLabel={t("bankings.has_invoice")}
                                searchParams={searchParams}
                                setSearchParams={setSearchParams}
                            />,
                            <DateRangeFilter
                                labelFrom={t("bankings.transfer_date_from")}
                                labelTo={t("bankings.transfer_date_to")}
                                filterName={"transfer_date"}
                                searchParams={searchParams}
                                setSearchParams={setSearchParams}
                            />,
                            // <ChangeInvoiceIsBookedButton selectedInvoices={selectedInvoices} onSubmit={handleBookedInvoice}/>
                        ]}
                        extraButtonListMdGrid={[2, 2, 3]}
                    />
                </Grid>
                <Grid item xs={12}>
                    {tableComponent()}
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
